import { useMemo } from 'react';
import fileConfig from '../../entity-config.json';

type TConfig = {
  VITE_MODE: string;
  VITE_ENTITY: string;
  VITE_URL_ID_BACKEND: string;
  VITE_CUSTOMER_AGREEMENT: string;
  VITE_PRIVACY_POLICY: string;
  VITE_INSTRUCTIONS_URL: string;
  VITE_TRADING_VIEW_URL: string;
  VITE_URL_CORE: string;
  VITE_TRADING_VIEW_REPLAY_URL: string;
  VITE_CLIENT_PORTAL_URL: string;
  VITE_SOCIAL_TRADING_URL: string;
  VITE_SUPER_CHARTS_URL: string;
  VITE_HELP_DESK_URL: string;
  VITE_INSTANT_TOP_UP_URL: string;
  VITE_IB_PANEL_URL: string;
  VITE_DEFAULT_REGISTRATION_COUNTRY: string;
  VITE_SET_REGISTRATION_LINK: boolean | string;
  VITE_CLIENT_PORTAL_SUPPORTED_LOCALES: string[];
  VITE_URL_BIZTECH: string;
  VITE_URL_PROMO: string;
  VITE_APPLICATION_TITLE: string;
  VITE_SECURITY_URL: string;
  VITE_URL_CREATE_ACCOUNT: string;
  VITE_URL_TRADING_API: string;
  VITE_ACCOUNT_MASK: boolean;
  VITE_B2CORE_OAUTH_ENABLED: boolean;
  VITE_URL_IB: string;
  VITE_IB_MOCKS: boolean;
  VITE_IB_DESK_ENABLED: boolean;
  VITE_IB_FEEDBACK_EMAIL: string;
  VITE_IB_SWITCH_BONUS_TERMS_URL: string;
  IS_SHOW_CERTIFICATES_DEBUG_MODE: boolean;
  IS_SHOW_ONBOARDING_DEBUG_MODE: boolean;
};

const defaultConfig: TConfig = {
  VITE_MODE: '',
  VITE_ENTITY: '',
  VITE_URL_ID_BACKEND: '',
  VITE_CUSTOMER_AGREEMENT: '',
  VITE_PRIVACY_POLICY: '',
  VITE_INSTRUCTIONS_URL: '',
  VITE_TRADING_VIEW_URL: '',
  VITE_URL_CORE: '',
  VITE_TRADING_VIEW_REPLAY_URL: '',
  VITE_CLIENT_PORTAL_URL: '',
  VITE_SOCIAL_TRADING_URL: '',
  VITE_SUPER_CHARTS_URL: '',
  VITE_HELP_DESK_URL: '',
  VITE_INSTANT_TOP_UP_URL: '',
  VITE_IB_PANEL_URL: '',
  VITE_DEFAULT_REGISTRATION_COUNTRY: '',
  VITE_SET_REGISTRATION_LINK: false,
  VITE_CLIENT_PORTAL_SUPPORTED_LOCALES: [],
  VITE_URL_BIZTECH: '',
  VITE_URL_PROMO: '',
  VITE_APPLICATION_TITLE: '',
  VITE_SECURITY_URL: '',
  VITE_URL_CREATE_ACCOUNT: '',
  VITE_URL_TRADING_API: '',
  VITE_ACCOUNT_MASK: false,
  VITE_B2CORE_OAUTH_ENABLED: false,
  VITE_URL_IB: '',
  VITE_IB_MOCKS: false,
  VITE_IB_DESK_ENABLED: false,
  VITE_IB_FEEDBACK_EMAIL: '',
  VITE_IB_SWITCH_BONUS_TERMS_URL: '',
  IS_SHOW_CERTIFICATES_DEBUG_MODE: false,
  IS_SHOW_ONBOARDING_DEBUG_MODE: false,
};

function parseBool(val: unknown, fallback: boolean): boolean {
  if (val === true || val === 'true') return true;

  if (val === false || val === 'false') return false;

  return fallback;
}

export const getEntityConfig = (): TConfig => {
  try {
    const parsedConfig = fileConfig[window.location.hostname as keyof typeof fileConfig];

    if (!parsedConfig) {
      console.error(`Unknown config for ${window.location.hostname}`);

      return defaultConfig;
    }

    const isCertificatesDebugModeAllowed =
      window.location.hostname === 'localhost' || import.meta.env.VITE_MODE === 'staging';

    const config = {
      VITE_MODE: import.meta.env.VITE_MODE || 'development',
      VITE_ENTITY: import.meta.env.VITE_ENTITY || parsedConfig.VITE_ENTITY,
      VITE_URL_ID_BACKEND: import.meta.env.VITE_URL_ID_BACKEND || parsedConfig.VITE_URL_ID_BACKEND,
      VITE_CUSTOMER_AGREEMENT: import.meta.env.VITE_CUSTOMER_AGREEMENT || parsedConfig.VITE_CUSTOMER_AGREEMENT,
      VITE_PRIVACY_POLICY: import.meta.env.VITE_PRIVACY_POLICY || parsedConfig.VITE_PRIVACY_POLICY,
      VITE_INSTRUCTIONS_URL: import.meta.env.VITE_INSTRUCTIONS_URL || parsedConfig.VITE_INSTRUCTIONS_URL,
      VITE_TRADING_VIEW_URL: import.meta.env.VITE_TRADING_VIEW_URL || parsedConfig.VITE_TRADING_VIEW_URL,
      VITE_URL_CORE: import.meta.env.VITE_URL_CORE || parsedConfig.VITE_URL_CORE,
      VITE_TRADING_VIEW_REPLAY_URL:
        import.meta.env.VITE_TRADING_VIEW_REPLAY_URL || parsedConfig.VITE_TRADING_VIEW_REPLAY_URL,
      VITE_CLIENT_PORTAL_URL: import.meta.env.VITE_CLIENT_PORTAL_URL || parsedConfig.VITE_CLIENT_PORTAL_URL,
      VITE_SOCIAL_TRADING_URL: import.meta.env.VITE_SOCIAL_TRADING_URL || parsedConfig.VITE_SOCIAL_TRADING_URL,
      VITE_SUPER_CHARTS_URL: import.meta.env.VITE_SUPER_CHARTS_URL || parsedConfig.VITE_SUPER_CHARTS_URL,
      VITE_HELP_DESK_URL: import.meta.env.VITE_HELP_DESK_URL || parsedConfig.VITE_HELP_DESK_URL,
      VITE_INSTANT_TOP_UP_URL: import.meta.env.VITE_INSTANT_TOP_UP_URL || parsedConfig.VITE_INSTANT_TOP_UP_URL,
      VITE_IB_PANEL_URL: import.meta.env.VITE_IB_PANEL_URL || parsedConfig.VITE_IB_PANEL_URL,
      VITE_DEFAULT_REGISTRATION_COUNTRY:
        import.meta.env.VITE_DEFAULT_REGISTRATION_COUNTRY || parsedConfig.VITE_DEFAULT_REGISTRATION_COUNTRY,
      VITE_SET_REGISTRATION_LINK: import.meta.env.VITE_SET_REGISTRATION_LINK || parsedConfig.VITE_SET_REGISTRATION_LINK,
      VITE_CLIENT_PORTAL_SUPPORTED_LOCALES:
        import.meta.env.VITE_CLIENT_PORTAL_SUPPORTED_LOCALES || parsedConfig.VITE_CLIENT_PORTAL_SUPPORTED_LOCALES,
      VITE_URL_BIZTECH: import.meta.env.VITE_URL_BIZTECH || parsedConfig.VITE_URL_BIZTECH,
      VITE_URL_PROMO: import.meta.env.VITE_URL_PROMO || parsedConfig.VITE_URL_PROMO,
      VITE_APPLICATION_TITLE: import.meta.env.VITE_APPLICATION_TITLE || parsedConfig.VITE_APPLICATION_TITLE,
      VITE_SECURITY_URL: import.meta.env.VITE_SECURITY_URL || parsedConfig.VITE_SECURITY_URL,
      VITE_URL_CREATE_ACCOUNT: import.meta.env.VITE_URL_CREATE_ACCOUNT || parsedConfig.VITE_URL_CREATE_ACCOUNT,
      VITE_URL_TRADING_API: import.meta.env.VITE_URL_TRADING_API || parsedConfig.VITE_URL_TRADING_API,
      VITE_ACCOUNT_MASK: parseBool(import.meta.env.VITE_ACCOUNT_MASK, parsedConfig.VITE_ACCOUNT_MASK),
      VITE_B2CORE_OAUTH_ENABLED: parseBool(
        import.meta.env.VITE_B2CORE_OAUTH_ENABLED,
        parsedConfig.VITE_B2CORE_OAUTH_ENABLED,
      ),
      VITE_URL_IB: import.meta.env.VITE_URL_IB || parsedConfig.VITE_URL_IB,
      VITE_IB_MOCKS: parseBool(import.meta.env.VITE_IB_MOCKS, parsedConfig.VITE_IB_MOCKS),
      VITE_IB_DESK_ENABLED: parseBool(import.meta.env.VITE_IB_DESK_ENABLED, parsedConfig.VITE_IB_DESK_ENABLED),
      VITE_IB_FEEDBACK_EMAIL: import.meta.env.VITE_IB_FEEDBACK_EMAIL || parsedConfig.VITE_IB_FEEDBACK_EMAIL,
      VITE_IB_SWITCH_BONUS_TERMS_URL:
        import.meta.env.VITE_IB_SWITCH_BONUS_TERMS_URL || parsedConfig.VITE_IB_SWITCH_BONUS_TERMS_URL,
      IS_SHOW_CERTIFICATES_DEBUG_MODE: isCertificatesDebugModeAllowed && parsedConfig.IS_SHOW_CERTIFICATES_DEBUG_MODE,
      IS_SHOW_ONBOARDING_DEBUG_MODE:
        window.location.hostname === 'localhost' && parsedConfig.IS_SHOW_ONBOARDING_DEBUG_MODE,
    };

    return applyQueryOverrides(config);
  } catch (error) {
    console.error('Failed to load entity configuration', error);

    return defaultConfig;
  }
};

export const useEntityConfig = () => {
  const config = useMemo(() => {
    return getEntityConfig();
  }, []);

  return config;
};

function applyQueryOverrides(config: TConfig): TConfig {
  const searchParams = new URLSearchParams(window.location.search);
  const isDisableOAuth = searchParams.get('isDisableOAuth');

  if (isDisableOAuth !== null) {
    config.VITE_B2CORE_OAUTH_ENABLED = !parseBoolParam(isDisableOAuth);
  }

  return config;

  function parseBoolParam(value: string): boolean {
    const normalized = value.trim().toLowerCase();

    if (normalized === '' || normalized === '0' || normalized === 'false') {
      return false;
    }

    if (normalized === '1' || normalized === 'true') {
      return true;
    }

    return false;
  }
}
